import React, { useEffect, useState } from "react";
import { Dialog } from "@headlessui/react";
import Editor from "@monaco-editor/react";
import {
  HiOutlineCheck,
  HiOutlineCheckCircle,
  HiOutlineDocumentText,
  HiOutlineXMark,
  HiOutlineArrowPath,
  HiOutlineShieldCheck,
  HiOutlineUserCircle,
  HiOutlineCpuChip
} from "react-icons/hi2";
import {
  viewDataSource,
  getDataSourceOcrAudit,
  updateDataSourceContent,
  approveDataSource
} from "../../../services/knowledge";
import { useAppContext } from "../../../helpers/hooks/common-hook";

const secondaryButton = 'inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 disabled:opacity-50';

export default function OcrReviewModal({
  open,
  appId,
  dataSource,
  isAdmin = false,
  onClose,
  onApproved
}) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [content, setContent] = useState("");
  const [originalContent, setOriginalContent] = useState("");
  const [auditData, setAuditData] = useState(null);
  const [activeTab, setActiveTab] = useState("editor"); // 'editor' | 'audit'
  const [statusMsg, setStatusMsg] = useState("");

  const dataSourceId = dataSource?.dataSourceId;

  useEffect(() => {
    if (!open || !dataSourceId) return;

    let isMounted = true;
    setLoading(true);
    setStatusMsg("Loading document and audit telemetry...");

    Promise.all([
      viewDataSource(appId, dataSourceId, "text").then(async res => {
        if (res.status === 200) {
          if (res.response?.content !== undefined) {
            return res.response.content;
          }
          if (res.response?.url) {
            try {
              const raw = await fetch(res.response.url);
              if (!raw.ok) {
                throw new Error(`S3 returned HTTP ${raw.status}: ${raw.statusText}`);
              }
              return await raw.text();
            } catch (e) {
              return "// Unable to fetch extracted text from preview URL";
            }
          }
        }
        return "// No preview URL available";
      }),
      getDataSourceOcrAudit(appId, dataSourceId).then(res => {
        if (res.status === 200) {
          return res.response;
        }
        return null;
      })
    ]).then(([textData, audit]) => {
      if (!isMounted) return;
      setContent(textData || "");
      setOriginalContent(textData || "");
      setAuditData(audit);
      setStatusMsg("");
      setLoading(false);
    }).catch(err => {
      if (!isMounted) return;
      setStatusMsg("Failed to load: " + (err.message || "Unknown error"));
      setLoading(false);
    });

    return () => {
      isMounted = false;
    };
  }, [open, appId, dataSourceId]);

  const s3Audit = auditData?.audit || null;
  const telemetry = s3Audit?.telemetry || auditData?.telemetry || null;
  const inspection = s3Audit?.inspection || auditData?.inspection || null;
  const guardrails = s3Audit?.guardrails || auditData?.guardrails || null;
  const quality = s3Audit?.quality || auditData?.quality || null;

  const rawScore = auditData?.confidenceScore ?? dataSource?.confidenceScore ?? guardrails?.confidence_score ?? null;
  const confidenceScore = rawScore != null ? Number(rawScore) : null;
  const confidencePct = confidenceScore != null ? Math.round(confidenceScore * 100) : null;
  const isHighConfidence = confidencePct != null && confidencePct >= 85;
  const isMediumConfidence = confidencePct != null && confidencePct >= 60 && confidencePct < 85;

  const ocrReviewRequired = auditData?.ocrReviewRequired ?? Boolean(dataSource?.ocrReviewRequired);
  const adminReviewPending = auditData?.adminReviewRequired ?? Boolean(dataSource?.adminReviewRequired ?? dataSource?.reviewRequired);

  const durationMs = auditData?.ocrDurationMs
    ?? auditData?.totalPipelineDurationMs
    ?? dataSource?.ocrDurationMs
    ?? telemetry?.total_duration_ms
    ?? null;

  const pageCount = telemetry?.page_count ?? inspection?.total_pages ?? null;

  const provider = s3Audit?.provider
    || (auditData?.ocrProvider && auditData.ocrProvider !== "default" ? auditData.ocrProvider : null)
    || dataSource?.ocr?.provider
    || "openai";
  const model = telemetry?.model || auditData?.ocrModel || dataSource?.ocr?.model || null;

  // Scan quality & resolution
  const isLowQuality = Boolean(
    quality?.is_low_quality ||
    auditData?.ocrLowQuality ||
    dataSource?.ocrLowQuality
  );
  const detectedDpi = quality?.detected_min_dpi ?? auditData?.detectedDpi ?? dataSource?.detectedDpi ?? null;
  const thresholdDpi = quality?.threshold_dpi ?? 150;

  const { authUserInfo } = useAppContext();

  const lastReviewedBy = auditData?.lastReviewedBy || dataSource?.lastReviewedBy || null;
  const lastReviewedAt = auditData?.lastReviewedAt || dataSource?.lastReviewedAt || null;
  const reviewHistory = auditData?.reviewHistory || dataSource?.reviewHistory || [];

  const resolveReviewerInfo = (entry = {}) => {
    // Priority: Email > Name/Username > UUID / ID
    const email = entry.reviewerEmail || (entry.reviewedBy?.includes("@") ? entry.reviewedBy : null);
    const name = entry.reviewerName || entry.name || entry.username;
    const id = entry.reviewerId || entry.userId || (!entry.reviewedBy?.includes("@") ? entry.reviewedBy : null);

    const displayName = email || name || id || "Reviewer";
    const subId = id && id !== displayName ? id : null;

    return { displayName, subId };
  };

  const latestReviewerInfo = resolveReviewerInfo({
    reviewedBy: lastReviewedBy,
    reviewerEmail: auditData?.lastReviewedByEmail || dataSource?.lastReviewedByEmail,
    reviewerId: auditData?.lastReviewedById || dataSource?.lastReviewedById,
    reviewerName: auditData?.lastReviewedByName || dataSource?.lastReviewedByName,
  });

  const headerReviewer = lastReviewedBy ? latestReviewerInfo.displayName : null;

  const reviewerEmail = authUserInfo?.email || null;
  const reviewerId = authUserInfo?.sub || null;
  const reviewerName = authUserInfo?.name || authUserInfo?.["cognito:username"] || null;

  const handleSaveContent = async () => {
    setSaving(true);
    try {
      await updateDataSourceContent(appId, dataSourceId, content, {
        reviewedBy: reviewerEmail || reviewerName || reviewerId,
        reviewerEmail,
        reviewerId,
        reviewerName,
      });
      onApproved?.();
      onClose();
    } catch (err) {
      alert("Failed to save changes: " + (err.message || "Unknown error"));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAndApprove = async () => {
    setSaving(true);
    try {
      // Step 1: Update content and clear OCR review
      await updateDataSourceContent(appId, dataSourceId, content, {
        reviewedBy: reviewerEmail || reviewerName || reviewerId,
        reviewerEmail,
        reviewerId,
        reviewerName,
      });
      // Step 2: Grant admin approval
      await approveDataSource(appId, dataSourceId);
      onApproved?.();
      onClose();
    } catch (err) {
      alert("Failed to save and approve: " + (err.message || "Unknown error"));
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const close = () => {
    if (!saving) onClose();
  };

  return (
    <Dialog open onClose={close} className="relative z-50">
      <div className="fixed inset-0 bg-gray-950/45 backdrop-blur-sm" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-3 sm:p-6">
        <Dialog.Panel className="flex max-h-[calc(100dvh-3rem)] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl">
          {/* Header */}
          <header className="flex shrink-0 items-start justify-between gap-4 border-b border-gray-100 px-6 py-5">
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gray-100 text-gray-900 dark:text-brand-lime">
                <HiOutlineDocumentText className="h-5 w-5" />
              </span>
              <div>
                <Dialog.Title className="text-lg font-semibold tracking-tight text-gray-900 flex items-center gap-2.5">
                  OCR Review: {dataSourceId}
                  {confidencePct != null && (
                    <span
                      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${isHighConfidence
                        ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300"
                        : isMediumConfidence
                          ? "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300"
                          : "bg-violet-50 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300"
                        }`}
                    >
                      {confidencePct}% Confidence
                    </span>
                  )}
                </Dialog.Title>
                <Dialog.Description className="mt-1 text-sm text-gray-500">
                  Provider: <strong className="font-medium text-gray-700">{provider}</strong>
                  {model && <span className="text-gray-500"> ({model})</span>}
                  {" • "}
                  {durationMs != null ? `${durationMs.toLocaleString()}ms` : "Completed"}
                  {headerReviewer && (
                    <span className="text-gray-600">
                      {" "}• Reviewed by <strong className="font-medium">{headerReviewer}</strong>
                    </span>
                  )}
                </Dialog.Description>
              </div>
            </div>

            <button
              type="button"
              onClick={close}
              disabled={saving}
              aria-label="Close review dialog"
              className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 focus-visible:ring-2 focus-visible:ring-gray-400 disabled:opacity-40"
            >
              <HiOutlineXMark className="h-5 w-5" />
            </button>
          </header>

          {/* Navigation Tabs */}
          <div className="flex shrink-0 border-b border-gray-100 bg-gray-50/70 px-6">
            <button
              type="button"
              onClick={() => setActiveTab("editor")}
              className={`py-3 px-4 text-sm font-medium border-b-2 transition ${activeTab === "editor"
                ? "border-gray-900 text-gray-900"
                : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
            >
              Extracted Text & Editor
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("audit")}
              className={`py-3 px-4 text-sm font-medium border-b-2 transition ${activeTab === "audit"
                ? "border-gray-900 text-gray-900"
                : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
            >
              Telemetry & Guardrails
            </button>
          </div>

          {/* Body */}
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden p-6">
            {loading ? (
              <div className="flex h-full min-h-[400px] flex-col items-center justify-center gap-3">
                <HiOutlineArrowPath className="h-7 w-7 animate-spin text-gray-400" />
                <p className="text-sm text-gray-500">{statusMsg}</p>
              </div>
            ) : activeTab === "editor" ? (
              <div className="flex flex-1 flex-col overflow-hidden space-y-2">
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span className="font-medium text-gray-700">
                    Markdown / Text Content (Edit as needed before approving)
                  </span>
                  {content !== originalContent && (
                    <span className="inline-flex items-center gap-1 font-medium text-amber-600 dark:text-amber-400">
                      <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                      Modified
                    </span>
                  )}
                </div>
                <div className="overflow-hidden rounded-xl border border-gray-200 bg-gray-900 shadow-sm">
                  <Editor
                    height="440px"
                    defaultLanguage="markdown"
                    language="markdown"
                    value={content || ""}
                    theme="vs-dark"
                    onChange={val => setContent(val || "")}
                    loading={
                      <div className="flex h-[440px] items-center justify-center text-xs text-gray-400">
                        <HiOutlineArrowPath className="h-5 w-5 animate-spin mr-2" />
                        Loading editor…
                      </div>
                    }
                    options={{
                      minimap: { enabled: false },
                      wordWrap: "on",
                      fontSize: 13,
                      scrollBeyondLastLine: false,
                      automaticLayout: true,
                    }}
                  />
                </div>
              </div>
            ) : (
              <div className="min-h-0 flex-1 overflow-y-auto space-y-5 pr-1">
                {/* Top 4 KPI Cards */}
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <div className="rounded-xl border border-gray-200 bg-gray-50/70 p-3.5">
                    <span className="text-xs text-gray-500">Confidence</span>
                    <p className="mt-1 text-base font-semibold text-gray-900">
                      {confidencePct != null ? `${confidencePct}%` : "N/A"}
                    </p>
                  </div>
                  <div className="rounded-xl border border-gray-200 bg-gray-50/70 p-3.5">
                    <span className="text-xs text-gray-500">Review Required</span>
                    <p className="mt-1 text-base font-semibold text-gray-900">
                      {ocrReviewRequired ? "Yes" : "No"}
                    </p>
                  </div>
                  <div className="rounded-xl border border-gray-200 bg-gray-50/70 p-3.5">
                    <span className="text-xs text-gray-500">Duration</span>
                    <p className="mt-1 text-base font-semibold text-gray-900">
                      {durationMs != null ? `${durationMs.toLocaleString()} ms` : "N/A"}
                    </p>
                  </div>
                  <div className="rounded-xl border border-gray-200 bg-gray-50/70 p-3.5">
                    <span className="text-xs text-gray-500">Pages Processed</span>
                    <p className="mt-1 text-base font-semibold text-gray-900">
                      {pageCount != null ? pageCount : "N/A"}
                    </p>
                  </div>
                </div>

                {/* Low Quality Alert Banner */}
                {isLowQuality && (
                  <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4 dark:border-amber-900/40 dark:bg-amber-950/20">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-xs font-semibold uppercase tracking-wide text-amber-800 dark:text-amber-300">
                          Scan Quality Alert
                        </span>
                        <p className="mt-1 text-sm font-semibold text-amber-900 dark:text-amber-200">
                          Low Quality ({detectedDpi != null ? `${detectedDpi} DPI` : "<150 DPI"})
                        </p>
                      </div>
                      <span className="rounded-md bg-amber-100 px-2 py-1 text-[11px] font-medium text-amber-800 dark:bg-amber-900/50 dark:text-amber-300">
                        Threshold: {thresholdDpi} DPI
                      </span>
                    </div>
                    <p className="mt-2 text-xs text-amber-700 dark:text-amber-400">
                      Document resolution is below the recommended {thresholdDpi} DPI threshold. Check the extracted text in the editor tab for potential OCR artifacts before approving.
                    </p>
                  </div>
                )}

                {/* Vision Model & Token Telemetry Card */}
                {telemetry ? (
                  <div className="rounded-xl border border-gray-200 p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-medium text-gray-900 flex items-center gap-2">
                        <HiOutlineCpuChip className="h-4 w-4 text-gray-500" />
                        Vision Model & Token Telemetry
                      </h4>
                      <span className="text-xs text-gray-500">
                        {provider} • {model || "default"}
                      </span>
                    </div>

                    <div className="grid grid-cols-3 gap-3 text-xs">
                      <div className="rounded-xl border border-gray-100 bg-gray-50/70 p-3">
                        <span className="text-gray-500 text-xs">Total Tokens</span>
                        <p className="font-semibold text-gray-900 mt-1 text-sm">
                          {telemetry.tokens?.total_tokens != null ? telemetry.tokens.total_tokens.toLocaleString() : "N/A"}
                        </p>
                      </div>
                      <div className="rounded-xl border border-gray-100 bg-gray-50/70 p-3">
                        <span className="text-gray-500 text-xs">Prompt Tokens</span>
                        <p className="font-semibold text-gray-900 mt-1 text-sm">
                          {telemetry.tokens?.prompt_tokens != null ? telemetry.tokens.prompt_tokens.toLocaleString() : "N/A"}
                        </p>
                      </div>
                      <div className="rounded-xl border border-gray-100 bg-gray-50/70 p-3">
                        <span className="text-gray-500 text-xs">Completion Tokens</span>
                        <p className="font-semibold text-gray-900 mt-1 text-sm">
                          {telemetry.tokens?.completion_tokens != null ? telemetry.tokens.completion_tokens.toLocaleString() : "N/A"}
                        </p>
                      </div>
                    </div>

                    {/* Per-page metrics breakdown table */}
                    {telemetry.page_metrics && telemetry.page_metrics.length > 0 && (
                      <div className="overflow-x-auto rounded-xl border border-gray-200">
                        <table className="w-full text-left text-xs text-gray-600">
                          <thead className="bg-gray-50/70 text-[11px] font-semibold uppercase tracking-wider text-gray-500 border-b border-gray-200">
                            <tr>
                              <th className="py-2.5 px-3.5">Page</th>
                              <th className="py-2.5 px-3.5">Duration</th>
                              <th className="py-2.5 px-3.5">Tokens</th>
                              <th className="py-2.5 px-3.5">Chars</th>
                              <th className="py-2.5 px-3.5">Scan DPI</th>
                              <th className="py-2.5 px-3.5">Status</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {telemetry.page_metrics.map((pm, idx) => (
                              <tr key={idx} className="hover:bg-gray-50/60">
                                <td className="py-2.5 px-3.5 font-medium text-gray-900">
                                  Page {pm.page_index != null ? pm.page_index + 1 : idx + 1}
                                </td>
                                <td className="py-2.5 px-3.5">
                                  {pm.duration_ms != null ? `${(pm.duration_ms / 1000).toFixed(1)}s` : "N/A"}
                                </td>
                                <td className="py-2.5 px-3.5">
                                  {pm.total_tokens != null ? pm.total_tokens.toLocaleString() : "N/A"}
                                </td>
                                <td className="py-2.5 px-3.5">
                                  {pm.char_count != null ? pm.char_count.toLocaleString() : 0}
                                </td>
                                <td className="py-2.5 px-3.5 text-gray-500">
                                  {pm.geometry?.rendered_dpi ? `${pm.geometry.rendered_dpi} DPI` : "N/A"}
                                </td>
                                <td className="py-2.5 px-3.5">
                                  {pm.error ? (
                                    <span className="text-rose-600 dark:text-rose-400 font-medium">Error</span>
                                  ) : pm.is_source_blank ? (
                                    <span className="text-amber-600 dark:text-amber-400">Blank</span>
                                  ) : (
                                    <span className="text-emerald-600 dark:text-emerald-400 font-medium">OK</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="rounded-xl border border-gray-200 p-4 bg-gray-50/50 text-xs text-gray-500">
                    <p>Detailed per-page audit telemetry is not available in storage. Showing DynamoDB record metrics.</p>
                  </div>
                )}

                {/* Guardrails Verification Card */}
                <div className="rounded-xl border border-gray-200 p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium text-gray-900 flex items-center gap-2">
                      <HiOutlineShieldCheck className="h-4 w-4 text-gray-500" />
                      Guardrail Rules & Verification
                    </h4>
                    <span className={`text-[11px] font-medium rounded-full px-2.5 py-0.5 ${ocrReviewRequired
                      ? "bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300"
                      : "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300"
                      }`}>
                      {ocrReviewRequired ? "Review Flagged" : "Passed"}
                    </span>
                  </div>

                  {guardrails?.rule_results && guardrails.rule_results.length > 0 ? (
                    <div className="space-y-2">
                      {guardrails.rule_results.map((rule, idx) => (
                        <div key={idx} className="flex items-center justify-between text-xs py-1.5 border-b border-gray-100 last:border-0">
                          <div>
                            <span className="font-medium text-gray-800">{rule.name || rule.rule_id}</span>
                            {rule.message && <p className="text-[11px] text-gray-500 mt-0.5">{rule.message}</p>}
                          </div>
                          <span className={`font-semibold text-xs ${rule.passed ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"}`}>
                            {rule.passed ? "Passed" : "Failed"}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500">
                      {guardrails?.enabled === false
                        ? "Automated baseline extraction passed (custom guardrail rule suite disabled)."
                        : "Standard document extraction verified successfully."}
                    </p>
                  )}
                </div>

                {/* Review & Audit Trail Card */}
                {(lastReviewedBy || reviewHistory.length > 0) && (
                  <div className="rounded-xl border border-gray-200 p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-medium text-gray-900 flex items-center gap-2">
                        <HiOutlineUserCircle className="h-4 w-4 text-gray-500" />
                        Review & Audit Trail
                      </h4>
                      {reviewHistory.length > 0 && (
                        <span className="text-xs text-gray-400">
                          {reviewHistory.length} {reviewHistory.length === 1 ? "entry" : "entries"}
                        </span>
                      )}
                    </div>

                    <div className="space-y-2 text-xs text-gray-600 max-h-48 overflow-y-auto pr-1 divide-y divide-gray-100">
                      {reviewHistory.length > 0 ? (
                        reviewHistory.map((entry, idx) => {
                          const info = resolveReviewerInfo(entry);
                          return (
                            <div key={idx} className="flex items-start justify-between py-2 first:pt-0 last:pb-0">
                              <div className="flex flex-col">
                                <span className="font-medium text-gray-900">
                                  {info.displayName}
                                </span>
                                {info.subId && (
                                  <span className="text-[10px] text-gray-400 font-mono tracking-tight mt-0.5">
                                    ID: {info.subId}
                                  </span>
                                )}
                              </div>
                              <div className="text-right flex flex-col items-end">
                                <span className="text-[11px] text-gray-500">
                                  {entry.reviewedAt ? new Date(entry.reviewedAt).toLocaleString() : ""}
                                </span>
                                <span className="inline-flex items-center rounded-md bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium capitalize text-gray-600 mt-0.5">
                                  {entry.action ? entry.action.replace(/_/g, " ") : "reviewed"}
                                </span>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div className="flex items-start justify-between py-1">
                          <div className="flex flex-col">
                            <span className="font-medium text-gray-900">
                              {latestReviewerInfo.displayName}
                            </span>
                            {latestReviewerInfo.subId && (
                              <span className="text-[10px] text-gray-400 font-mono tracking-tight mt-0.5">
                                ID: {latestReviewerInfo.subId}
                              </span>
                            )}
                          </div>
                          <span className="text-[11px] text-gray-400">
                            {lastReviewedAt ? new Date(lastReviewedAt).toLocaleString() : ""}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Collapsible Raw JSON Payload */}
                <details className="rounded-xl border border-gray-200 overflow-hidden text-xs">
                  <summary className="cursor-pointer select-none px-4 py-3 font-medium text-gray-600 bg-gray-50/70 hover:bg-gray-100/70 transition flex items-center justify-between">
                    <span>Raw Audit JSON Payload</span>
                    <span className="text-[11px] text-gray-400">Click to expand</span>
                  </summary>
                  <pre className="p-3 text-[11px] bg-gray-900 text-gray-100 overflow-x-auto max-h-60 leading-relaxed font-mono">
                    {JSON.stringify(s3Audit || auditData || dataSource || { message: "No audit telemetry found for this data source." }, null, 2)}
                  </pre>
                </details>
              </div>
            )}
          </div>

          {/* Footer */}
          <footer className="flex shrink-0 flex-wrap items-center justify-end gap-3 border-t border-gray-100 bg-gray-50/70 px-6 py-4">
            {statusMsg && (
              <span role="status" className="mr-auto text-xs text-gray-500">
                {statusMsg}
              </span>
            )}
            <button
              type="button"
              onClick={close}
              disabled={saving}
              className={secondaryButton}
            >
              Cancel
            </button>

            {isAdmin && adminReviewPending && (
              <button
                type="button"
                onClick={handleSaveAndApprove}
                disabled={saving || loading}
                className={secondaryButton}
              >
                <HiOutlineCheckCircle className="h-4 w-4" />
                Save & Approve KB
              </button>
            )}

            <button
              type="button"
              onClick={handleSaveContent}
              disabled={saving || loading || (content === originalContent && !ocrReviewRequired)}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-gray-900 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 disabled:opacity-60"
            >
              {saving ? (
                <HiOutlineArrowPath className="h-4 w-4 animate-spin motion-reduce:animate-none" />
              ) : (
                <HiOutlineCheck className="h-4 w-4" />
              )}
              {content !== originalContent ? "Save Changes" : ocrReviewRequired ? "Mark as Reviewed" : "Save"}
            </button>
          </footer>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
